import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Divider,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { httpClient } from '../../../../../config';

export const MarkupRateSection = ({ sponsoredSettingId, industryId, isCreateMode = false }) => {
  const [currentMarkupRate, setCurrentMarkupRate] = useState(null);
  const [historicMarkups, setHistoricMarkups] = useState([]);
  const [newMarkupRate, setNewMarkupRate] = useState('');
  const [newMarkupDate, setNewMarkupDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isCreateMode && sponsoredSettingId) {
      fetchCurrentMarkupRate();
      fetchHistoricMarkups();
    }
  }, [sponsoredSettingId, isCreateMode]);

  const fetchCurrentMarkupRate = async () => {
    try {
      const response = await httpClient(
        `/tables/report_sponsored_settings/markupRate/${sponsoredSettingId}`
      );
      const data = JSON.parse(response.body);
      setCurrentMarkupRate(data || null);
    } catch (err) {
      console.error('Error fetching current markup rate:', err);
    }
  };

  const fetchHistoricMarkups = async () => {
    try {
      const response = await httpClient(
        `/tables/report_sponsored_settings/markupRates/${sponsoredSettingId}`
      );
      const data = JSON.parse(response.body);
      setHistoricMarkups(data || []);
    } catch (err) {
      console.error('Error fetching historic markups:', err);
    }
  };

  const handleSaveMarkup = async () => {
    setError('');
    setSuccess('');

    // Validation
    const markupValue = parseFloat(newMarkupRate);
    if (isNaN(markupValue) || markupValue < 0 || markupValue > 100) {
      setError('Markup rate must be between 0 and 100');
      return;
    }

    if (!newMarkupDate) {
      setError('Please select a date');
      return;
    }

    setLoading(true);

    try {
      await httpClient(`/tables/report_sponsored_settings/markupRate/${sponsoredSettingId}`, {
        method: 'POST',
        body: JSON.stringify({
          markup_rate: markupValue / 100,
          markup_date: newMarkupDate,
          industry_id: industryId,
        }),
      });

      setSuccess('Markup rate saved successfully');
      setNewMarkupRate('');
      setNewMarkupDate(format(new Date(), 'yyyy-MM-dd'));
      
      // Refresh data
      fetchCurrentMarkupRate();
      fetchHistoricMarkups();
    } catch (err) {
      console.error('Error saving markup rate:', err);
      setError('Failed to save markup rate. It may already exist for this date.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMarkup = async (id) => {
    if (!window.confirm('Are you sure you want to delete this markup rate?')) {
      return;
    }

    try {
      await httpClient(`/tables/report_sponsored_settings/markupRate/${id}`, {
        method: 'DELETE',
        body: JSON.stringify({ industry_id: industryId }),
      });

      setSuccess('Markup rate deleted successfully');
      
      // Refresh data
      fetchCurrentMarkupRate();
      fetchHistoricMarkups();
    } catch (err) {
      console.error('Error deleting markup rate:', err);
      setError('Failed to delete markup rate');
    }
  };

  const formatMarkupRateForDisplay = (rate) => {
    if (rate === null || rate === undefined) return 'N/A';
    return `${(parseFloat(rate) * 100).toFixed(2)}%`;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  if (isCreateMode) {
    return (
      <Box sx={{ mt: 3 }}>
        <Divider sx={{ mb: 2 }} />
        <Alert severity="info">
          Markup rate settings will be available after creating the sponsored setting.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Current markup rate</Typography>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {formatMarkupRateForDisplay(currentMarkupRate)}
        </Typography>
      </Box>

      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
        Setup Historic markup
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 3 }}>
        <TextField
          label="Markup Rate (%)"
          type="number"
          value={newMarkupRate}
          onChange={(e) => setNewMarkupRate(e.target.value)}
          inputProps={{ min: 0, max: 100, step: 0.01 }}
          sx={{ width: '200px' }}
          size="small"
        />
        <TextField
          label="Date"
          type="date"
          value={newMarkupDate}
          onChange={(e) => setNewMarkupDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: '200px' }}
          size="small"
        />
        <Button
          variant="contained"
          onClick={handleSaveMarkup}
          disabled={loading}
          sx={{ height: '40px' }}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ maxHeight: 400, mt: 2 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Historic Setup</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Markup rate</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '100px' }} align="center">
                Delete
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {historicMarkups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No historic markups found
                </TableCell>
              </TableRow>
            ) : (
              historicMarkups.map((markup) => (
                <TableRow key={markup.id}>
                  <TableCell>{formatDateForDisplay(markup.markup_date)}</TableCell>
                  <TableCell>{formatMarkupRateForDisplay(markup.markup_rate)}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleDeleteMarkup(markup.id)}
                      title="Delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

